import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmDialog } from './ConfirmDialog.jsx';
import { Icons as I } from './pack-icons';
import { useTimezoneSettings } from '../hooks/useTimezoneSettings.js';
import { formatNowIn, formatOffset, isValidTimeZone, listTimeZones } from '../lib/timezone.js';
import './timezone-settings.css';

export function TimezoneSettings({ controller, showSave = true }) {
  const { t, i18n } = useTranslation();
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => {
      if (typeof document === 'undefined' || !document.hidden) setTick(previous => previous + 1);
    }, 30000);
    return () => clearInterval(timer);
  }, []);
  const groups = useMemo(() => listTimeZones(new Date()), []);
  const options = useMemo(() => {
    if (controller.value && isValidTimeZone(controller.value)
      && !groups.some(group => group.zones.some(zone => zone.value === controller.value))) {
      return [{ region: 'Current', zones: [{ value: controller.value, label: `${controller.value} (${formatOffset(controller.value)})` }] }, ...groups];
    }
    return groups;
  }, [groups, controller.value]);
  const currentTime = useMemo(
    () => formatNowIn(controller.value, i18n.language, new Date()),
    [controller.value, i18n.language, tick],
  );
  return <div className="timezone-settings">
    <p className="caption muted">{t('settings.timezoneDefaultHint')}</p>
    {controller.readErrors.length > 0 && <p className="auth-error" role="alert">
      {t('settings.timezoneLoadFailed', { target: controller.readErrors.join(', ') })}
    </p>}
    {!controller.initialized ? <p className="caption muted">{t('settings.loading')}</p>
      : controller.savedValue === null && <p className="caption muted">{t('settings.timezoneNotSet')}</p>}
    <label className="timezone-settings-field">
      <span>{t('settings.timezoneTitle')}</span>
      <select className="input ep-input" value={controller.value}
        onChange={event => controller.setValue(event.target.value)}
        disabled={!controller.initialized || controller.saving} aria-label={t('settings.timezoneTitle')}>
        {options.map(group => <optgroup key={group.region} label={group.region}>
          {group.zones.map(zone => <option key={zone.value} value={zone.value}>{zone.label}</option>)}
        </optgroup>)}
      </select>
    </label>
    <p className="timezone-settings-current">{t('settings.timezoneCurrentTime', { time: currentTime })}</p>
    <div className="timezone-settings-actions">
      <button type="button" className="btn timezone-settings-device" onClick={controller.useDevice}
        disabled={!controller.initialized || controller.saving}>{t('settings.timezoneUseDevice')}</button>
      {showSave && <button type="button" className="btn btn-primary" onClick={controller.save}
        disabled={!controller.canSave}>{t('settings.timezoneSave')}</button>}
    </div>
  </div>;
}

export function DashboardTimezone() {
  const { t } = useTranslation();
  const controller = useTimezoneSettings();
  const [open, setOpen] = useState(false);
  const close = () => { if (!controller.saving) { controller.reset(); setOpen(false); } };
  useEffect(() => {
    if (!open) return undefined;
    const onKey = event => { if (event.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, controller.saving, controller.savedValue]);
  const label = !controller.initialized ? t('settings.loading')
    : controller.savedValue || t('settings.timezoneSuggested', { zone: controller.value });
  return <>
    <button type="button" className="btn timezone-settings-trigger" aria-haspopup="dialog"
      onClick={() => setOpen(true)} title={t('settings.timezoneTitle')}>
      <I.clock size={14} /> <span>{t('settings.timezoneTitle')}: {label}</span>
    </button>
    {open && <div className="timezone-dialog">
      <ConfirmDialog open={open} title={t('settings.timezoneTitle')}
        confirmLabel={t('settings.timezoneSave')} confirmingLabel={t('settings.saving')}
        cancelLabel={t('common.close')} confirming={controller.saving} confirmDisabled={!controller.canSave}
        onCancel={close} onConfirm={async () => { if (await controller.save()) setOpen(false); }}>
        <TimezoneSettings controller={controller} showSave={false} />
      </ConfirmDialog>
    </div>}
  </>;
}
